import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { SaldoAFavor } from '@/components/SaldoAFavor';
import { HistorialCobros } from '@/components/HistorialCobros';
import { movimientosDeCobro, resumenDeFacturacion } from '@/lib/historialCobros';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Phone, Mail, Building2, MapPin, Calendar, 
  Clock, CheckCircle, XCircle, Circle, Edit, Trash2, Filter, TicketIcon, Cloud, Lock, Unlock, Send, MessageSquare, Receipt
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { SepaMandate } from '@/components/SepaMandate';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { confirmAsync } from '@/lib/confirm';
import { mensajeDeError } from '@/lib/errores';
import { escaparHtml } from '@/lib/escapar-html';

export default function ClientDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  
  const [client, setClient] = useState(null);
  const [actions, setActions] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Facturas del cliente
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  // Que no cargaran no es que no tenga: sin esto la pestaña decía «todavía no
  // tiene facturas» y «no consta ningún cobro», y soporte se lo repetía al cliente.
  const [errorFacturas, setErrorFacturas] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Email/WhatsApp Templates states
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);
  const [selectedWhatsappTemplate, setSelectedWhatsappTemplate] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [companyConfig, setCompanyConfig] = useState(null);
  
  // Dialog states
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [selectedActionType, setSelectedActionType] = useState(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Partner states
  const [partners, setPartners] = useState([]);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  
  // C10 Platform states
  const [creatingPlatform, setCreatingPlatform] = useState(false);
  const [deletingPlatform, setDeletingPlatform] = useState(false);
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const [blockingPlatform, setBlockingPlatform] = useState(false);
  const [unblockingPlatform, setUnblockingPlatform] = useState(false);
  const [platformCredentials, setPlatformCredentials] = useState(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);

  useEffect(() => {
    fetchClientData();
    fetchActionTypes();
    fetchUsers();
    fetchTickets();
    fetchQuotes();
    fetchInvoices();
    fetchEmailTemplates();
    fetchWhatsAppTemplates();
    fetchCompanyConfig();
    fetchPartners();
  }, [id]);

  const fetchPartners = async () => {
    try {
      const response = await axiosInstance.get('/partners?estado=activo');
      setPartners(response.data);
    } catch (error) {
      console.error('Error loading partners');
    }
  };

  const fetchClientData = async () => {
    try {
      setLoading(true);
      const [clientRes, actionsRes] = await Promise.all([
        axiosInstance.get(`/customers/${id}`),
        axiosInstance.get(`/customers/${id}/actions`)
      ]);
      setClient(clientRes.data);
      setActions(actionsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos del cliente');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchActionTypes = async () => {
    try {
      const response = await axiosInstance.get('/action-types');
      setActionTypes(response.data);
    } catch (error) {
      console.error('Error loading action types');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users');
    }
  };

  const fetchTickets = async () => {
    try {
      // Pasamos customer_id al backend → evitamos N+1 virtual de filtrar 5k tickets en JS
      const response = await axiosInstance.get('/tickets', { params: { customer_id: id } });
      setTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets');
    }
  };

  const fetchQuotes = async () => {
    try {
      // Idem: filtrado server-side
      const response = await axiosInstance.get('/quotes', { params: { customer_id: id } });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error loading quotes');
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoadingInvoices(true);
      setErrorFacturas(false);
      const response = await axiosInstance.get(`/invoices/customer/${id}`);
      setInvoices(response.data || []);
    } catch (error) {
      setErrorFacturas(true);
      // Un fallo aqui no puede tumbar la ficha entera: el resto de pestañas
      // sigue siendo util aunque facturacion no responda.
      console.error('Error cargando facturas del cliente');
      toast.error(mensajeDeError(error, 'No se pudieron cargar las facturas'));
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleOpenInvoice = async (invoice) => {
    // Se abre ya con lo que tenemos de la lista para que el dialogo no
    // parpadee en blanco, y se completa con el detalle del servidor (NIF y
    // direccion del cliente, que la factura no lleva copiados).
    setSelectedInvoice(invoice);
    setInvoiceDialogOpen(true);
    try {
      setLoadingInvoiceDetail(true);
      const response = await axiosInstance.get(`/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
    } catch (error) {
      toast.error(mensajeDeError(error, 'No se pudo cargar el detalle de la factura'));
    } finally {
      setLoadingInvoiceDetail(false);
    }
  };

  const handleDownloadInvoicePdf = async (invoice) => {
    try {
      setDownloadingPdf(true);
      const response = await axiosInstance.get(`/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${invoice.invoice_number || invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(mensajeDeError(error, 'Error al descargar el PDF'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axiosInstance.get('/email-templates');
      setEmailTemplates(response.data);
    } catch (error) {
      console.error('Error loading email templates');
    }
  };

  const fetchWhatsAppTemplates = async () => {
    try {
      const response = await axiosInstance.get('/whatsapp-templates');
      setWhatsappTemplates(response.data);
    } catch (error) {
      console.error('Error loading WhatsApp templates');
    }
  };

  const fetchCompanyConfig = async () => {
    try {
      const response = await axiosInstance.get('/company-config');
      setCompanyConfig(response.data);
    } catch (error) {
      console.error('Error loading company config');
    }
  };

  // Replace variables in template with client data and company data
  // `transformar` se aplica a cada dato, no a la plantilla: la vista previa de un
  // email pinta HTML y le pasa escaparHtml, porque el nombre de una ficha lo puede
  // escribir un visitante de una landing.
  const replaceVariables = (text, transformar = (dato) => dato) => {
    if (!text || !client) return text;
    
    const valor = (dato) => transformar(dato || '');
    let result = text;
    
    // Client variables
    result = result.replace(/{nombre_cliente}/g, () => valor(client.name));
    result = result.replace(/{email}/g, () => valor(client.email));
    result = result.replace(/{empresa}/g, () => valor(client.company));
    result = result.replace(/{telefono}/g, () => valor(client.phone));
    result = result.replace(/{nombre_comercial}/g, () => valor(client.trade_name));
    result = result.replace(/{nombre_fiscal}/g, () => valor(client.fiscal_name));
    result = result.replace(/{direccion}/g, () => valor(client.address));
    result = result.replace(/{ciudad}/g, () => valor(client.city));
    result = result.replace(/{codigo_postal}/g, () => valor(client.postal_code));
    result = result.replace(/{provincia}/g, () => valor(client.province));
    
    // Company variables
    if (companyConfig) {
      result = result.replace(/{telefono_comercial}/g, () => valor(companyConfig.telefono_comercial));
      result = result.replace(/{telefono_ventas}/g, () => valor(companyConfig.telefono_ventas));
      result = result.replace(/{whatsapp_comercial}/g, () => valor(companyConfig.whatsapp_comercial));
      result = result.replace(/{whatsapp_ventas}/g, () => valor(companyConfig.whatsapp_ventas));
      result = result.replace(/{email_comercial}/g, () => valor(companyConfig.email_comercial));
      result = result.replace(/{email_administracion}/g, () => valor(companyConfig.email_administracion));
      result = result.replace(/{email_general}/g, () => valor(companyConfig.email_general));
      result = result.replace(/{email_ventas}/g, () => valor(companyConfig.email_ventas));
      result = result.replace(/{pagina_web}/g, () => valor(companyConfig.pagina_web));
      result = result.replace(/{linkedin}/g, () => valor(companyConfig.linkedin));
      result = result.replace(/{facebook}/g, () => valor(companyConfig.facebook));
      result = result.replace(/{instagram}/g, () => valor(companyConfig.instagram));
      result = result.replace(/{tiktok}/g, () => valor(companyConfig.tiktok));
      result = result.replace(/{twitter_x}/g, () => valor(companyConfig.twitter_x));
      result = result.replace(/{google_my_business}/g, () => valor(companyConfig.google_my_business));
    }
    
    return result;
  };

  const handleSendEmail = async () => {
    if (!selectedEmailTemplate) {
      toast.error('Selecciona una plantilla de email');
      return;
    }
    
    if (selectedEmails.length === 0) {
      toast.error('Selecciona al menos un destinatario');
      return;
    }
    
    if (!(await confirmAsync({ title: 'Enviar email', description: `¿Enviar email a ${selectedEmails.length} destinatario(s)?`, confirmText: 'Enviar' }))) {
      return;
    }
    
    setSendingEmail(true);
    
    try {
      const response = await axiosInstance.post('/send-template-email', {
        template_id: selectedEmailTemplate.id,
        customer_id: id,
        to_emails: selectedEmails
      });
      
      if (response.data.success) {
        toast.success(`✅ Email enviado correctamente`);
        setSelectedEmails([]);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al enviar email';
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const validateAndFormatPhone = (phone) => {
    if (!phone) return null;
    
    // Remove spaces, dashes, parentheses
    let cleaned = phone.replace(/[\s\-\(\)]/g, '');
    
    // If starts with 00, replace with +
    if (cleaned.startsWith('00')) {
      cleaned = '+' + cleaned.substring(2);
    }
    
    // If doesn't start with +, assume it's Spanish (+34)
    if (!cleaned.startsWith('+')) {
      cleaned = '+34' + cleaned;
    }
    
    // Validate format: should be + followed by digits
    const phoneRegex = /^\+\d{10,15}$/;
    if (!phoneRegex.test(cleaned)) {
      return null;
    }
    
    return cleaned;
  };

  const handleSendWhatsApp = () => {
    if (!selectedWhatsappTemplate) {
      toast.error('Selecciona una plantilla de WhatsApp');
      return;
    }
    
    const formattedPhone = validateAndFormatPhone(phoneNumber || client.phone);
    
    if (!formattedPhone) {
      toast.error('El número de teléfono no es válido. Por favor corrige el formato: +34612345678');
      return;
    }
    
    // Update phone in state if it was formatted
    if (formattedPhone !== phoneNumber) {
      setPhoneNumber(formattedPhone);
    }
    
    const message = replaceVariables(selectedWhatsappTemplate.message);
    const whatsappUrl = `https://wa.me/${formattedPhone.substring(1)}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    toast.success('Abriendo WhatsApp...');
  };

  const handleCreateAction = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const actionTypeId = formData.get('action_type_id');
    const selectedActionType = actionTypes.find(at => at.id === actionTypeId);
    
    const data = {
      client_id: id,
      action_type_id: actionTypeId,
      title: formData.get('title') || '',
      description: formData.get('description') || '',
      scheduled_date: formData.get('scheduled_date') ? new Date(formData.get('scheduled_date')).toISOString() : null,
      assigned_user_id: formData.get('assigned_user_id')
    };

    try {
      if (editingAction) {
        await axiosInstance.put(`/actions/${editingAction.id}`, data);
        toast.success('Acción actualizada');
        setActionDialogOpen(false);
        setEditingAction(null);
        fetchClientData();
      } else {
        // Create action (will be marked as completed if creates_quote is true)
        await axiosInstance.post('/actions', data);
        
        // If creates_quote, navigate to quotes after creating action
        if (selectedActionType?.creates_quote) {
          setActionDialogOpen(false);
          toast.success('Redirigiendo a crear presupuesto...');
          navigate(`/quotes?customer=${id}&actionType=${actionTypeId}&title=${encodeURIComponent(formData.get('title') || '')}&description=${encodeURIComponent(formData.get('description') || '')}`);
        } else {
          toast.success('Acción creada');
          setActionDialogOpen(false);
          setEditingAction(null);
          fetchClientData();
        }
      }
    } catch (error) {
      toast.error('Error al guardar acción');
    }
  };

  const handleStatusChange = async (actionId, newStatus) => {
    try {
      await axiosInstance.patch(`/actions/${actionId}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      fetchClientData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDeleteAction = async (actionId) => {
    if (!confirm('¿Eliminar esta acción?')) return;
    
    try {
      await axiosInstance.delete(`/actions/${actionId}`);
      toast.success('Acción eliminada');
      fetchClientData();
    } catch (error) {
      toast.error('Error al eliminar acción');
    }
  };

  const openEditDialog = (action) => {
    setEditingAction(action);
    setActionDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingAction(null);
    setActionDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendiente', class: 'bg-yellow-100 text-yellow-800', icon: Circle },
      completed: { label: 'Completada', class: 'bg-green-100 text-green-800', icon: CheckCircle },
      not_done: { label: 'No Realizada', class: 'bg-red-100 text-red-800', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={config.class}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getActionTypeColor = (actionTypeId) => {
    const actionType = actionTypes.find(t => t.id === actionTypeId);
    return actionType?.color || 'gray';
  };

  const handleCreatePlatform = async () => {
    if (!(await confirmAsync({ title: 'Crear plataforma C10', description: 'Este proceso creará:\n1. Plataforma en servidor C10\n2. Registro DNS en OVH (si está configurado)', confirmText: 'Crear' }))) {
      return;
    }
    
    setCreatingPlatform(true);
    
    try {
      const response = await axiosInstance.post(`/customers/${id}/create-platform`);
      
      if (response.data.success) {
        setPlatformCredentials(response.data.data);
        setShowCredentialsDialog(true);
        
        // Reload client data to show new credentials
        await fetchClientData();
        
        // Check complete process result
        if (response.data.dns_created) {
          toast.success('✅ Proceso completado:\n• Plataforma C10 creada\n• DNS configurado en OVH', {
            duration: 5000
          });
        } else {
          toast.warning(`⚠️ Proceso parcial:\n• Plataforma C10 creada ✓\n• DNS: ${response.data.dns_message}\n\nPuedes crear el DNS manualmente con el botón "Crear DNS"`, {
            duration: 6000
          });
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al crear la plataforma';
      toast.error(`❌ Error en el proceso:\n${errorMsg}`);
    } finally {
      setCreatingPlatform(false);
    }
  };

  const handleCreateDNS = async () => {
    if (!(await confirmAsync({ title: 'Crear DNS en OVH', description: '¿Deseas crear el registro DNS para esta plataforma en OVH?', confirmText: 'Crear DNS' }))) {
      return;
    }
    
    try {
      const response = await axiosInstance.post(`/customers/${id}/create-dns`);
      
      if (response.data.success) {
        toast.success('✅ DNS creado correctamente en OVH');
        // Reload client data to show updated DNS status
        await fetchClientData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al crear el DNS';
      toast.error(`❌ Error al crear DNS:\n${errorMsg}`);
    }
  };

  const handleAssignPartner = async () => {
    if (!selectedPartnerId) {
      toast.error('Selecciona un partner');
      return;
    }
    
    try {
      await axiosInstance.post(`/partners/${selectedPartnerId}/assign-client?client_id=${id}`);
      toast.success('Partner asignado correctamente');
      setShowPartnerDialog(false);
      setSelectedPartnerId('');
      fetchClientData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar partner');
    }
  };

  const handleRemovePartner = async () => {
    if (!client?.partner_id) return;
    if (!(await confirmAsync({ title: 'Desasignar partner', description: '¿Seguro que quieres desasignar el partner de este cliente?', confirmText: 'Desasignar' }))) return;
    
    try {
      await axiosInstance.post(`/partners/${client.partner_id}/unassign-client/${id}`);
      toast.success('Partner desasignado correctamente');
      fetchClientData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desasignar partner');
    }
  };

  const handleDeletePlatform = async () => {
    // Primera confirmación
    const first = await confirm({
      title: 'Borrar plataforma C10',
      description:
        '⚠️ ATENCIÓN: ¿Estás seguro de que deseas borrar la plataforma C10?\n\n' +
        'Esta acción:\n' +
        '• Borrará la plataforma del servidor C10\n' +
        '• Eliminará el registro DNS de OVH\n' +
        '• Borrará todas las credenciales guardadas',
      confirmText: 'Continuar',
      destructive: true,
    });
    if (!first) return;

    // Segunda confirmación
    const second = await confirm({
      title: '🚨 Confirmación final',
      description: '¿Estás COMPLETAMENTE SEGURO?\n\nEsta acción NO se puede deshacer.',
      confirmText: 'Sí, borrar',
      destructive: true,
    });
    if (!second) return;
    
    setDeletingPlatform(true);
    
    try {
      const response = await axiosInstance.delete(`/customers/${id}/delete-platform`);
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}\n\n• ${response.data.details.platform}\n• ${response.data.details.dns}`, {
          duration: 6000
        });
        
        // Reload client data
        await fetchClientData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al borrar la plataforma';
      toast.error(`❌ Error:\n${errorMsg}`);
    } finally {
      setDeletingPlatform(false);
    }
  };

  const handleSendCredentials = async () => {
    if (!(await confirmAsync({ title: '📧 Enviar credenciales', description: `¿Enviar credenciales de la plataforma C10 a ${client.platform_email}?`, confirmText: 'Enviar' }))) {
      return;
    }
    
    setSendingCredentials(true);
    
    try {
      const response = await axiosInstance.post(`/customers/${id}/send-credentials`);
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`, {
          duration: 4000
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al enviar credenciales';
      toast.error(`❌ ${errorMsg}`, {
        duration: 5000
      });
    } finally {
      setSendingCredentials(false);
    }
  };

  const handleBlockPlatform = async () => {
    const ok = await confirm({
      title: '🔒 Bloquear plataforma C10',
      description: '¿Estás seguro? El cliente no podrá acceder hasta que la desbloquees.',
      confirmText: 'Bloquear',
      destructive: true,
    });
    if (!ok) return;
    
    setBlockingPlatform(true);
    
    try {
      const response = await axiosInstance.post(`/customers/${id}/block-platform`);
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`, {
          duration: 4000
        });
        
        // Reload client data to update blocked status
        await fetchClientData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al bloquear la plataforma';
      toast.error(`❌ ${errorMsg}`, {
        duration: 5000
      });
    } finally {
      setBlockingPlatform(false);
    }
  };

  const handleUnblockPlatform = async () => {
    const ok = await confirm({
      title: '🔓 Desbloquear plataforma C10',
      description: '¿Deseas desbloquear la plataforma? El cliente podrá volver a acceder normalmente.',
      confirmText: 'Desbloquear',
    });
    if (!ok) return;
    
    setUnblockingPlatform(true);
    
    try {
      const response = await axiosInstance.post(`/customers/${id}/unblock-platform`);
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`, {
          duration: 4000
        });
        
        // Reload client data to update blocked status
        await fetchClientData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al desbloquear la plataforma';
      toast.error(`❌ ${errorMsg}`, {
        duration: 5000
      });
    } finally {
      setUnblockingPlatform(false);
    }
  };

  const filteredActions = actions.filter(action => {
    if (statusFilter !== 'all' && action.status !== statusFilter) return false;
    if (typeFilter !== 'all' && action.action_type_id !== typeFilter) return false;
    
    if (dateFilter !== 'all') {
      const actionDate = parseISO(action.scheduled_date);
      const now = new Date();
      
      if (dateFilter === 'future' && actionDate < now) return false;
      if (dateFilter === 'past' && actionDate > now) return false;
      if (dateFilter === 'today') {
        const isToday = actionDate.toDateString() === now.toDateString();
        if (!isToday) return false;
      }
    }
    
    return true;
  });

  const estadoFactura = (status) => {
    const estados = {
      draft: { label: 'Borrador', class: 'bg-gray-100 text-gray-800' },
      accepted: { label: 'Emitida', class: 'bg-blue-100 text-blue-800' },
      paid: { label: 'Pagada', class: 'bg-green-100 text-green-800' },
      overdue: { label: 'Vencida', class: 'bg-red-100 text-red-800' },
      returned: { label: 'Devuelta', class: 'bg-purple-100 text-purple-800' },
      // Sin estas dos, una factura con el recibo devuelto o cobrada a medias
      // salía aquí como «Borrador». Mismos colores que en Facturas.
      recibo_devuelto: { label: 'Recibo devuelto', class: 'bg-amber-100 text-amber-900' },
      parcial: { label: 'Cobro parcial', class: 'bg-sky-100 text-sky-800' }
    };
    return estados[status] || estados.draft;
  };

  const formatearImporte = (valor) =>
    `${(Number(valor) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  const formatearFecha = (valor) => {
    if (!valor) return '—';
    try {
      return format(new Date(valor), "d MMM yyyy", { locale: es });
    } catch {
      return '—';
    }
  };

  // Las plantillas recurrentes viven en la misma coleccion que las facturas,
  // con estado 'accepted' y sin numero. Mezclarlas con las facturas reales en
  // la ficha del cliente haria creer que se le ha facturado algo que todavia
  // no se ha expedido, asi que van en su propio bloque.
  const plantillasRecurrentes = invoices.filter((f) => f.is_recurring);
  const facturasReales = invoices.filter((f) => !f.is_recurring);

  const resumenFacturacion = resumenDeFacturacion(facturasReales);
  const movimientos = movimientosDeCobro(facturasReales);

  if (loading) {
    // Skeleton que imita el layout real para reducir "flash of blank screen"
    return (
      <Layout user={user}>
        <div className="space-y-4 p-4">
          <Skeleton className="h-9 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
          <Skeleton className="h-10 w-full max-w-md" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!client) return null;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(location.state?.volverA || '/clients')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-4xl font-bold text-[#212B35]">{client.name}</h1>
                {client.email_marketing_opt_out && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full border border-red-200" data-testid="opt-out-badge">
                    Opt-out Marketing
                  </span>
                )}
                {client.client_status && client.client_status !== 'active' && (
                  <span className={`px-3 py-1 text-sm font-medium rounded-full border ${
                    client.client_status === 'cancelled' ? 'bg-gray-100 text-gray-700 border-gray-200' :
                    client.client_status === 'pending_cancel' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                    'bg-orange-100 text-orange-700 border-orange-200'
                  }`} data-testid="client-status-badge">
                    {client.client_status === 'cancelled' ? 'Cancelado' : client.client_status === 'pending_cancel' ? 'Baja pendiente' : 'Inactivo'}
                  </span>
                )}
              </div>
              <p className="text-[#212B35]/70">{client.company}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={openCreateDialog}
              className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Acción
            </Button>
            {/* Facturar desde aquí es un atajo, no otra pantalla: lleva al
                alta de Facturas con este cliente ya puesto. Un segundo
                formulario de factura acabaría divergiendo del primero.

                Solo para clientes: a un lead no se le factura, y ofrecer un
                botón que siempre va a decir que no es peor que no tenerlo. */}
            {client.type === 'customer' && (
              <Button
                onClick={() => navigate(`/invoices?nueva=${client.id}`)}
                variant="outline"
                className="border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17] hover:text-white"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Facturar
              </Button>
            )}
            <Button
              onClick={() => navigate('/tickets', { state: { customerId: client.id, customerName: client.name } })}
              variant="outline"
              className="border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17] hover:text-white"
            >
              <TicketIcon className="w-4 h-4 mr-2" />
              Nuevo Ticket
            </Button>
          </div>
        </div>

        <Tabs defaultValue="actions" className="w-full">
          <TabsList>
            <TabsTrigger value="actions">Acciones</TabsTrigger>
            <TabsTrigger value="quotes">Presupuestos</TabsTrigger>
            <TabsTrigger value="invoices">Facturas</TabsTrigger>
            <TabsTrigger value="tickets">Tickets</TabsTrigger>
            <TabsTrigger value="impagos">⚠️ Impagos</TabsTrigger>
            <TabsTrigger value="email">Envío Email</TabsTrigger>
            <TabsTrigger value="whatsapp">Envío WhatsApp</TabsTrigger>
            <TabsTrigger value="sepa">Mandato SEPA</TabsTrigger>
            <TabsTrigger value="info">Información</TabsTrigger>
          </TabsList>

          {/* ============= TAB: ACCIONES ============= */}
          <TabsContent value="actions" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Estado</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="pending">Pendientes</SelectItem>
                        <SelectItem value="completed">Completadas</SelectItem>
                        <SelectItem value="not_done">No Realizadas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo</Label>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {actionTypes.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fecha</Label>
                    <Select value={dateFilter} onValueChange={setDateFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="today">Hoy</SelectItem>
                        <SelectItem value="future">Futuras</SelectItem>
                        <SelectItem value="past">Pasadas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions Timeline */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Histórico de Acciones ({filteredActions.length})
                </h3>
              </div>

              {filteredActions.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No hay acciones registradas</p>
                    <Button
                      onClick={openCreateDialog}
                      variant="outline"
                      className="mt-4"
                    >
                      Crear primera acción
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredActions.map((action) => {
                    const actionType = actionTypes.find(t => t.id === action.action_type_id);
                    const color = actionType?.color || 'gray';
                    
                    return (
                      <Card key={action.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div className={`p-3 rounded-lg bg-${color}-100 flex-shrink-0`}>
                              <Calendar className={`w-5 h-5 text-${color}-600`} />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                  <h4 className="font-semibold text-lg">{action.title}</h4>
                                  <Badge className={`bg-${color}-100 text-${color}-800 mt-1`}>
                                    {action.action_type_name}
                                  </Badge>
                                </div>
                                {getStatusBadge(action.status)}
                              </div>

                              {action.description && (
                                <p className="text-sm text-gray-600 mb-3">{action.description}</p>
                              )}

                              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {format(parseISO(action.scheduled_date), "d 'de' MMMM yyyy, HH:mm", { locale: es })}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Building2 className="w-4 h-4" />
                                  {action.assigned_user_name}
                                </div>
                              </div>

                              {/* Action Buttons */}
                              {action.status === 'pending' && (
                                <div className="flex gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => handleStatusChange(action.id, 'completed')}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Completar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleStatusChange(action.id, 'not_done')}
                                  >
                                    <XCircle className="w-3 h-3 mr-1" />
                                    No Realizada
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditDialog(action)}
                                  >
                                    <Edit className="w-3 h-3 mr-1" />
                                    Editar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600"
                                    onClick={() => handleDeleteAction(action.id)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============= TAB: TICKETS ============= */}
          <TabsContent value="tickets">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Tickets de Soporte</CardTitle>
                  <Button
                    size="sm"
                    onClick={() => navigate('/tickets', { state: { customerId: client.id, customerName: client.name } })}
                    className="bg-[#9C0F17] hover:bg-[#7A0C12]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Ticket
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {tickets.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No hay tickets registrados para este cliente
                  </p>
                ) : (
                  <div className="space-y-3">
                    {tickets.map(ticket => (
                      <div 
                        key={ticket.id}
                        className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/tickets/${ticket.id}`)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-mono text-sm text-gray-600">{ticket.ticket_number}</span>
                              {ticket.status === 'open' && <Badge className="bg-blue-100 text-blue-800">Nuevo</Badge>}
                              {ticket.status === 'waiting_tech' && <Badge className="bg-orange-100 text-orange-800">En Progreso</Badge>}
                              {ticket.status === 'waiting_client' && <Badge className="bg-yellow-100 text-yellow-800">Espera Cliente</Badge>}
                              {ticket.status === 'closed' && <Badge className="bg-gray-100 text-gray-800">Cerrado</Badge>}
                              {ticket.priority === 'urgent' && <Badge className="bg-red-600 text-white">URGENTE</Badge>}
                            </div>
                            <h4 className="font-semibold">{ticket.subject}</h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {ticket.assigned_to_name ? `Asignado a ${ticket.assigned_to_name}` : 'Sin asignar'}
                            </p>
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(ticket.created_at).toLocaleDateString('es-ES')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============= TAB: IMPAGOS ============= */}
          <TabsContent value="impagos" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-yellow-600">⚠️</span>
                  Impagos / Recobros
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!client?.impagos || client.impagos.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>✅ No hay impagos pendientes</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {client.impagos.map((impago, idx) => (
                      <div key={idx} className="border rounded-lg p-4 bg-yellow-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">Factura: {impago.invoice_number}</p>
                            <p className="text-lg font-bold text-red-600">{impago.amount?.toFixed(2)} €</p>
                            <p className="text-xs text-gray-500">
                              Creado: {new Date(impago.created_at).toLocaleDateString('es-ES')}
                            </p>
                          </div>
                          {impago.stripe_link && (
                            <div className="text-right">
                              <p className="text-xs text-gray-500 mb-1">Link de pago:</p>
                              <a 
                                href={impago.stripe_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                              >
                                💳 Pagar con Stripe
                              </a>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(impago.stripe_link);
                                  toast.success('Link copiado');
                                }}
                                className="block mt-1 text-xs text-blue-600 hover:underline"
                              >
                                📋 Copiar link
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============= TAB: PRESUPUESTOS ============= */}
          <TabsContent value="quotes" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Presupuestos ({quotes.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {quotes.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay presupuestos para este cliente</p>
                ) : (
                  <div className="space-y-3">
                    {quotes.map((quote) => (
                      <div
                        key={quote.id}
                        className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <button
                                onClick={() => navigate(`/quotes`)}
                                className="font-mono text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                              >
                                {quote.quote_number}
                              </button>
                              {quote.status === 'sent' && (
                                <Badge className="bg-blue-100 text-blue-800">Enviado</Badge>
                              )}
                              {quote.status === 'accepted' && (
                                <Badge className="bg-green-100 text-green-800">Aceptado</Badge>
                              )}
                              {quote.status === 'rejected' && (
                                <Badge className="bg-red-100 text-red-800">Rechazado</Badge>
                              )}
                              {quote.status === 'draft' && (
                                <Badge className="bg-gray-100 text-gray-800">Borrador</Badge>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span>Creado: {format(new Date(quote.created_at), "d 'de' MMMM yyyy", { locale: es })}</span>
                              </div>
                              <div className="font-semibold text-gray-900">
                                Total: {quote.total_with_tax ? quote.total_with_tax.toFixed(2) : quote.total ? quote.total.toFixed(2) : '0.00'}€
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============= TAB: FACTURAS ============= */}
          <TabsContent value="invoices" className="space-y-4">
            {loadingInvoices ? (
              <Card>
                <CardContent className="p-6 space-y-3">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </CardContent>
              </Card>
            ) : errorFacturas ? (
              <Card className="border-amber-300">
                <CardContent className="p-6 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-amber-900">
                    No se pudieron cargar las facturas ni los pagos de este cliente.
                    No quiere decir que no tenga.
                  </p>
                  <Button variant="outline" size="sm" onClick={fetchInvoices}>
                    Reintentar
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Resumen. Es lo primero que se pregunta al abrir las facturas
                    de un cliente: cuanto se le ha facturado y cuanto debe. */}
                {facturasReales.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-sm text-gray-500">Facturado</p>
                        <p className="text-2xl font-bold text-[#212B35]">
                          {formatearImporte(resumenFacturacion.facturado)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-sm text-gray-500">Cobrado</p>
                        <p className="text-2xl font-bold text-green-700">
                          {formatearImporte(resumenFacturacion.cobrado)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className={resumenFacturacion.pendiente > 0 ? 'border-amber-300' : undefined}>
                      <CardContent className="p-4">
                        <p className="text-sm text-gray-500">Pendiente de cobro</p>
                        <p className={`text-2xl font-bold ${resumenFacturacion.pendiente > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                          {formatearImporte(resumenFacturacion.pendiente)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Justo debajo de los totales: cuando un cliente llama
                    diciendo que ya pagó, es lo que se mira (Myriam, 31-oct-2026). */}
                {facturasReales.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Pagos y devoluciones</CardTitle>
                      <p className="text-sm text-gray-500">
                        Lo que ha entrado y lo que ha devuelto el banco, de todas sus facturas.
                      </p>
                    </CardHeader>
                    <CardContent>
                      <HistorialCobros
                        movimientos={movimientos}
                        conFactura
                        onAbrirFactura={handleOpenInvoice}
                      />
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle>Facturas ({facturasReales.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {facturasReales.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        Este cliente todavía no tiene facturas
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-gray-500">
                              <th className="p-3 font-medium">Número</th>
                              <th className="p-3 font-medium">Emisión</th>
                              <th className="p-3 font-medium">Vencimiento</th>
                              <th className="p-3 font-medium">Estado</th>
                              <th className="p-3 font-medium text-right">Total</th>
                              <th className="p-3" />
                            </tr>
                          </thead>
                          <tbody>
                            {facturasReales.map((factura) => {
                              const estado = estadoFactura(factura.status);
                              return (
                                <tr
                                  key={factura.id}
                                  onClick={() => handleOpenInvoice(factura)}
                                  className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                                >
                                  <td className="p-3 font-mono font-semibold text-blue-600">
                                    {factura.invoice_number || 'Sin número'}
                                  </td>
                                  <td className="p-3 text-gray-600">{formatearFecha(factura.issue_date)}</td>
                                  <td className="p-3 text-gray-600">{formatearFecha(factura.due_date)}</td>
                                  <td className="p-3">
                                    <div className="flex items-center gap-2">
                                      <Badge className={estado.class}>{estado.label}</Badge>
                                      {factura.is_return && (
                                        <Badge className="bg-orange-100 text-orange-800">Rectificativa</Badge>
                                      )}
                                      {factura.is_proforma && (
                                        <Badge className="bg-yellow-100 text-yellow-800">Proforma</Badge>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-3 text-right font-semibold">
                                    {formatearImporte(factura.total)}
                                  </td>
                                  <td className="p-3 text-right">
                                    <span className="text-xs text-blue-600">Ver detalle →</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* El saldo va justo encima de las repetitivas: es de
                    donde se va a ir consumiendo, asi que es donde se
                    mira (Angel, 7-oct-2026). */}
                <SaldoAFavor customerId={client?.id} customerName={client?.name} />

                {plantillasRecurrentes.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Facturación recurrente ({plantillasRecurrentes.length})
                      </CardTitle>
                      <p className="text-sm text-gray-500">
                        Plantillas que generan facturas solas. Todavía no son facturas emitidas.
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {plantillasRecurrentes.map((plantilla) => (
                        <div
                          key={plantilla.id}
                          onClick={() => handleOpenInvoice(plantilla)}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <div>
                            <p className="font-medium">
                              {plantilla.invoice_reference || 'Plantilla sin referencia'}
                            </p>
                            <p className="text-sm text-gray-500">
                              Próxima emisión: {formatearFecha(plantilla.next_creation_date)}
                              {plantilla.recurrence_frequency ? ` · ${plantilla.recurrence_frequency}` : ''}
                            </p>
                          </div>
                          <span className="font-semibold">{formatearImporte(plantilla.total)}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ============= TAB: INFORMACIÓN ============= */}
          <TabsContent value="info">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle>Datos del Cliente</CardTitle>
                  {!client.c10_url && (
                    <Button
                      onClick={handleCreatePlatform}
                      disabled={creatingPlatform}
                      className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white whitespace-nowrap"
                    >
                      {creatingPlatform ? 'Creando...' : '🖥️ Crear Plataforma C10'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-600">Nombre</Label>
                    <p className="font-semibold">{client.name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Empresa</Label>
                    <p className="font-semibold">{client.company || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Email</Label>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <p className="font-semibold">{client.email}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-600">Teléfono</Label>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <p className="font-semibold">{client.phone || '-'}</p>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-600">Dirección</Label>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                      <div>
                        <p className="font-semibold">{client.address || '-'}</p>
                        {client.city && (
                          <p className="text-sm text-gray-600">
                            {client.postal_code} {client.city}, {client.country}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  {client.tax_id && (
                    <div>
                      <Label className="text-gray-600">NIF/CIF</Label>
                      <p className="font-semibold">{client.tax_id}</p>
                    </div>
                  )}
                  {client.notes && (
                    <div className="col-span-2">
                      <Label className="text-gray-600">Notas</Label>
                      <p className="text-sm">{client.notes}</p>
                    </div>
                  )}
                </div>

                {/* Partner Asociado */}
                <div className="mt-6 p-4 bg-gray-50 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">Partner Asociado</h3>
                      </div>
                      {client.partner_id ? (
                        <div className="mt-2">
                          <p className="font-medium text-lg">{client.partner_name}</p>
                          <Badge className={client.partner_tipo === 'distribuidor' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                            {client.partner_tipo === 'distribuidor' ? 'DISTRIBUIDOR' : 'PRESCRIPTOR'}
                          </Badge>
                        </div>
                      ) : (
                        <p className="text-gray-500 mt-1">Cliente directo (sin partner)</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {client.partner_id ? (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleRemovePartner}
                          className="text-red-600 hover:bg-red-50"
                        >
                          Quitar Partner
                        </Button>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowPartnerDialog(true)}
                        >
                          <Building2 className="w-4 h-4 mr-1" />
                          Asignar Partner
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Marketing & GDPR */}
                <div className={`mt-6 p-4 border rounded-lg ${client.email_marketing_opt_out ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`} data-testid="marketing-gdpr-section">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                      <h3 className="font-semibold text-gray-900">Marketing y Comunicaciones</h3>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between p-3 bg-white border rounded-lg cursor-pointer">
                      <div>
                        <p className="font-medium text-sm text-red-700">No recibir comunicaciones de Camarero 10</p>
                        <p className="text-xs text-gray-500">Si activo, el cliente no recibira campanas ni emails comerciales</p>
                        {client.email_marketing_opt_out && client.email_marketing_opt_out_at && (
                          <p className="text-xs text-red-500 mt-1">
                            Baja: {new Date(client.email_marketing_opt_out_at).toLocaleDateString('es-ES')} 
                            {client.email_marketing_opt_out_source && ` (${client.email_marketing_opt_out_source === 'unsubscribe_link' ? 'enlace email' : client.email_marketing_opt_out_source === 'manual' ? 'manual CRM' : client.email_marketing_opt_out_source})`}
                          </p>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={client.email_marketing_opt_out || false}
                        onChange={async (e) => {
                          const optOut = e.target.checked;
                          let reason = '';
                          if (optOut) {
                            reason = window.prompt('Motivo del opt-out (opcional):') || '';
                          }
                          try {
                            await axiosInstance.post(`/marketing/opt-out/${client.id}`, { opt_out: optOut, reason });
                            toast.success(optOut ? 'Cliente excluido de marketing' : 'Cliente incluido en marketing');
                            fetchClientData();
                          } catch (err) { toast.error('Error al actualizar'); }
                        }}
                        className="w-5 h-5 rounded text-red-600 focus:ring-red-500"
                        data-testid="opt-out-toggle"
                      />
                    </label>
                    <div className="flex items-center gap-3">
                      <label className="text-xs text-gray-600">Estado cliente:</label>
                      <select
                        value={client.client_status || 'active'}
                        onChange={async (e) => {
                          try {
                            await axiosInstance.post(`/marketing/client-status/${client.id}`, { client_status: e.target.value });
                            toast.success('Estado actualizado');
                            fetchClientData();
                          } catch (err) { toast.error('Error'); }
                        }}
                        className="text-sm border rounded px-2 py-1"
                        data-testid="client-status-select"
                      >
                        <option value="active">Activo</option>
                        <option value="inactive">Inactivo</option>
                        <option value="pending_cancel">Baja pendiente</option>
                        <option value="cancelled">Cancelado</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Credenciales Plataforma C10 */}
                {client.c10_url && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-blue-900">🖥️ Acceso Plataforma C10</h3>
                        {client.c10_client_id && (
                          <p className="text-xs mt-1 text-gray-600">
                            ID: {client.c10_client_id}
                          </p>
                        )}
                        {client.c10_dns_created !== undefined && (
                          <p className={`text-xs mt-1 ${client.c10_dns_created ? 'text-green-700' : 'text-orange-700'}`}>
                            DNS: {client.c10_dns_created ? '✓ Configurado' : `⚠ ${client.c10_dns_message || 'No configurado'}`}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {!client.c10_dns_created && (
                          <Button
                            onClick={handleCreateDNS}
                            variant="outline"
                            size="sm"
                            className="border-blue-600 text-blue-600 hover:bg-blue-50"
                          >
                            <Cloud className="w-4 h-4 mr-2" />
                            Crear DNS
                          </Button>
                        )}
                        {!client.c10_blocked ? (
                          <Button
                            onClick={handleBlockPlatform}
                            disabled={blockingPlatform}
                            variant="outline"
                            size="sm"
                            className="border-orange-600 text-orange-600 hover:bg-orange-50"
                          >
                            <Lock className="w-4 h-4 mr-2" />
                            {blockingPlatform ? 'Bloqueando...' : 'Bloquear Plataforma'}
                          </Button>
                        ) : (
                          <Button
                            onClick={handleUnblockPlatform}
                            disabled={unblockingPlatform}
                            variant="outline"
                            size="sm"
                            className="border-green-600 text-green-600 hover:bg-green-50"
                          >
                            <Unlock className="w-4 h-4 mr-2" />
                            {unblockingPlatform ? 'Desbloqueando...' : 'Desbloquear Plataforma'}
                          </Button>
                        )}
                        <Button
                          onClick={handleSendCredentials}
                          disabled={sendingCredentials}
                          variant="outline"
                          size="sm"
                          className="border-green-600 text-green-600 hover:bg-green-50"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          {sendingCredentials ? 'Enviando...' : 'Mandar Credenciales'}
                        </Button>
                        <Button
                          onClick={handleDeletePlatform}
                          disabled={deletingPlatform}
                          variant="outline"
                          size="sm"
                          className="border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {deletingPlatform ? 'Borrando...' : 'Borrar Plataforma'}
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label className="text-gray-700 font-medium">URL Plataforma</Label>
                        <p className="text-sm font-mono bg-white p-2 rounded border mt-1">
                          <a href={`https://${client.c10_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {client.c10_url}
                          </a>
                        </p>
                      </div>
                      
                      <div>
                        <Label className="text-gray-700 font-medium">🔐 Administración</Label>
                        <div className="mt-2 space-y-1 text-sm">
                          <p>
                            <span className="text-gray-600">URL:</span>{' '}
                            <a href={`https://${client.c10_admin_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono">
                              {client.c10_admin_url}
                            </a>
                          </p>
                          <p>
                            <span className="text-gray-600">Usuario:</span> <span className="font-mono">{client.c10_admin_user}</span>
                          </p>
                          <p title="Las contraseñas se almacenan cifradas (hash bcrypt). Solo visibles una vez al crear la plataforma.">
                            <span className="text-gray-600">Contraseña:</span> <span className="font-mono font-semibold text-gray-400">••••••••</span>
                            <span className="ml-2 text-xs text-gray-400">(cifrada)</span>
                          </p>
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-700 font-medium">💳 TPV</Label>
                        <div className="mt-2 space-y-1 text-sm">
                          <p>
                            <span className="text-gray-600">URL:</span>{' '}
                            <a href={`https://${client.c10_tpv_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono">
                              {client.c10_tpv_url}
                            </a>
                          </p>
                          <p>
                            <span className="text-gray-600">Usuario:</span> <span className="font-mono">{client.c10_tpv_user}</span>
                          </p>
                          <p title="Las contraseñas se almacenan cifradas (hash bcrypt). Solo visibles una vez al crear la plataforma.">
                            <span className="text-gray-600">Contraseña:</span> <span className="font-mono font-semibold text-gray-400">••••••••</span>
                            <span className="ml-2 text-xs text-gray-400">(cifrada)</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============= TAB: MANDATO SEPA ============= */}

          {/* ============= TAB: ENVÍO EMAIL ============= */}
          <TabsContent value="email" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Enviar Plantilla de Email a {client?.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Template Selector */}
                <div>
                  <Label>Seleccionar Plantilla</Label>
                  <Select 
                    value={selectedEmailTemplate?.id || ''} 
                    onValueChange={(value) => {
                      const template = emailTemplates.find(t => t.id === value);
                      setSelectedEmailTemplate(template);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona una plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Email Recipients Selection */}
                <div>
                  <Label>Destinatarios</Label>
                  <div className="mt-2 space-y-2">
                    {client?.email && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`email-main-${client.email}`}
                          checked={selectedEmails.includes(client.email)}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (e.target.checked) {
                              setSelectedEmails(prev => [...prev, client.email]);
                            } else {
                              setSelectedEmails(prev => prev.filter(email => email !== client.email));
                            }
                          }}
                          className="rounded w-4 h-4 cursor-pointer"
                        />
                        <label htmlFor={`email-main-${client.email}`} className="text-sm cursor-pointer">
                          Email principal: <span className="font-mono">{client.email}</span>
                        </label>
                      </div>
                    )}
                    {client?.platform_email && client.platform_email !== client.email && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`email-platform-${client.platform_email}`}
                          checked={selectedEmails.includes(client.platform_email)}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (e.target.checked) {
                              setSelectedEmails(prev => [...prev, client.platform_email]);
                            } else {
                              setSelectedEmails(prev => prev.filter(email => email !== client.platform_email));
                            }
                          }}
                          className="rounded w-4 h-4 cursor-pointer"
                        />
                        <label htmlFor={`email-platform-${client.platform_email}`} className="text-sm cursor-pointer">
                          Email plataforma: <span className="font-mono">{client.platform_email}</span>
                        </label>
                      </div>
                    )}
                    {client?.billing_email && client.billing_email !== client.email && client.billing_email !== client.platform_email && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`email-billing-${client.billing_email}`}
                          checked={selectedEmails.includes(client.billing_email)}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (e.target.checked) {
                              setSelectedEmails(prev => [...prev, client.billing_email]);
                            } else {
                              setSelectedEmails(prev => prev.filter(email => email !== client.billing_email));
                            }
                          }}
                          className="rounded w-4 h-4 cursor-pointer"
                        />
                        <label htmlFor={`email-billing-${client.billing_email}`} className="text-sm cursor-pointer">
                          Email facturación: <span className="font-mono">{client.billing_email}</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* Preview */}
                {selectedEmailTemplate && (
                  <div>
                    <Label>Vista Previa</Label>
                    <div className="mt-2 border rounded-lg p-4 bg-gray-50">
                      <div className="mb-4">
                        <p className="text-sm text-gray-600">Asunto:</p>
                        <p className="font-semibold">{replaceVariables(selectedEmailTemplate.subject)}</p>
                      </div>
                      <div className="prose max-w-none">
                        <div dangerouslySetInnerHTML={{ __html: replaceVariables(selectedEmailTemplate.body, escaparHtml) }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Send Button */}
                <div className="flex justify-end">
                  <Button 
                    onClick={handleSendEmail}
                    disabled={!selectedEmailTemplate || selectedEmails.length === 0 || sendingEmail}
                    className="bg-[#9C0F17] hover:bg-[#7A0C12]"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingEmail ? 'Enviando...' : 'Enviar Email'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============= TAB: ENVÍO WHATSAPP ============= */}
          <TabsContent value="whatsapp" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Enviar Plantilla de WhatsApp a {client?.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Template Selector */}
                <div>
                  <Label>Seleccionar Plantilla</Label>
                  <Select 
                    value={selectedWhatsappTemplate?.id || ''} 
                    onValueChange={(value) => {
                      const template = whatsappTemplates.find(t => t.id === value);
                      setSelectedWhatsappTemplate(template);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona una plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      {whatsappTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Phone Number */}
                <div>
                  <Label>Número de Teléfono</Label>
                  <Input
                    type="text"
                    value={phoneNumber || client?.phone || ''}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+34612345678"
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formato: +34612345678 (código de país + número)
                  </p>
                </div>

                {/* Preview */}
                {selectedWhatsappTemplate && (
                  <div>
                    <Label>Vista Previa</Label>
                    <div className="mt-2 border rounded-lg p-4 bg-green-50">
                      <div className="whitespace-pre-wrap font-sans text-sm">
                        {replaceVariables(selectedWhatsappTemplate.message)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Send Button */}
                <div className="flex justify-end">
                  <Button 
                    onClick={handleSendWhatsApp}
                    disabled={!selectedWhatsappTemplate || !phoneNumber}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Abrir WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sepa">
            <SepaMandate customerId={id} />
          </TabsContent>
        </Tabs>

        {/* ============= DIALOG: CREAR/EDITAR ACCIÓN ============= */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingAction ? 'Editar Acción' : 'Nueva Acción'}</DialogTitle>
              <DialogDescription>
                Planifica una acción comercial para {client.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAction} className="space-y-4">
              <div>
                <Label>Tipo de Acción *</Label>
                <Select 
                  name="action_type_id" 
                  defaultValue={editingAction?.action_type_id} 
                  required
                  onValueChange={(value) => {
                    const type = actionTypes.find(t => t.id === value);
                    setSelectedActionType(type);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {actionTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedActionType?.creates_quote && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    ℹ️ Esta acción redirigirá automáticamente a crear un presupuesto. Los campos título y fecha son opcionales.
                  </p>
                </div>
              )}

              <div>
                <Label>Título/Asunto {!selectedActionType?.creates_quote && '*'}</Label>
                <Input
                  name="title"
                  defaultValue={editingAction?.title}
                  placeholder={selectedActionType?.creates_quote ? "Se generará automáticamente" : "ej: Llamada de seguimiento"}
                  required={!selectedActionType?.creates_quote}
                />
              </div>

              <div>
                <Label>Descripción/Notas</Label>
                <Textarea
                  name="description"
                  defaultValue={editingAction?.description}
                  placeholder="Detalles de la acción..."
                  rows={3}
                />
              </div>

              <div>
                <Label>Fecha y Hora {!selectedActionType?.creates_quote && '*'}</Label>
                <Input
                  type="datetime-local"
                  name="scheduled_date"
                  defaultValue={editingAction?.scheduled_date ? format(parseISO(editingAction.scheduled_date), "yyyy-MM-dd'T'HH:mm") : ''}
                  required={!selectedActionType?.creates_quote}
                />
                {selectedActionType?.creates_quote && (
                  <p className="text-xs text-gray-500 mt-1">Se usará la fecha y hora actual si se deja vacío</p>
                )}
              </div>

              <div>
                <Label>Usuario Asignado *</Label>
                <Select name="assigned_user_id" defaultValue={editingAction?.assigned_user_id || user?.id || ''} required>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full bg-[#9C0F17] hover:bg-[#7A0C12] text-white">
                {editingAction ? 'Actualizar' : 'Crear'} Acción
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ============= DIALOG: CREDENCIALES C10 ============= */}
        <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>✅ Plataforma C10 Creada Exitosamente</DialogTitle>
              <DialogDescription>
                Guarda estas credenciales de acceso. También las podrás ver en la pestaña de Información del cliente.
                {client.c10_url && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                    <strong>Nota:</strong> Verifica que el DNS esté propagado antes de intentar acceder a la plataforma. Esto puede tardar unos minutos.
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {platformCredentials && (
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-3">🖥️ URL Plataforma</h3>
                  <a 
                    href={`https://${platformCredentials.url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-mono text-lg"
                  >
                    {platformCredentials.url}
                  </a>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-3">🔐 Administración</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <Label className="text-gray-600">URL</Label>
                        <a 
                          href={`https://${platformCredentials.admin_url}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block text-blue-600 hover:underline font-mono"
                        >
                          {platformCredentials.admin_url}
                        </a>
                      </div>
                      <div>
                        <Label className="text-gray-600">Usuario</Label>
                        <p className="font-mono bg-white p-2 rounded border">{platformCredentials.admin_user}</p>
                      </div>
                      <div>
                        <Label className="text-gray-600">Contraseña</Label>
                        <p className="font-mono font-semibold bg-white p-2 rounded border text-green-700">
                          {platformCredentials.admin_pass}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h3 className="font-semibold text-purple-900 mb-3">💳 TPV</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <Label className="text-gray-600">URL</Label>
                        <a 
                          href={`https://${platformCredentials.tpv_url}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block text-blue-600 hover:underline font-mono"
                        >
                          {platformCredentials.tpv_url}
                        </a>
                      </div>
                      <div>
                        <Label className="text-gray-600">Usuario</Label>
                        <p className="font-mono bg-white p-2 rounded border">{platformCredentials.tpv_user}</p>
                      </div>
                      <div>
                        <Label className="text-gray-600">Contraseña</Label>
                        <p className="font-mono font-semibold bg-white p-2 rounded border text-purple-700">
                          {platformCredentials.tpv_pass}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button onClick={() => setShowCredentialsDialog(false)} className="bg-[#9C0F17] hover:bg-[#7A0C12]">
                    Cerrar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Partner Assignment Dialog */}
        <Dialog open={showPartnerDialog} onOpenChange={setShowPartnerDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Asignar Partner</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Selecciona el partner al que quieres asociar este cliente.
              </p>
              
              <Select value={selectedPartnerId} onValueChange={setSelectedPartnerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un partner..." />
                </SelectTrigger>
                <SelectContent>
                  {partners.map(partner => (
                    <SelectItem key={partner.id} value={partner.id}>
                      <div className="flex items-center gap-2">
                        <span>{partner.nombre_comercial}</span>
                        <Badge className={partner.tipo === 'distribuidor' ? 'bg-blue-100 text-blue-800 text-xs' : 'bg-purple-100 text-purple-800 text-xs'}>
                          {partner.tipo === 'distribuidor' ? 'DIST' : 'PRESC'}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {partners.length === 0 && (
                <p className="text-sm text-amber-600">No hay partners activos disponibles</p>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPartnerDialog(false)}>Cancelar</Button>
              <Button onClick={handleAssignPartner} className="bg-[#9C0F17]" disabled={!selectedPartnerId}>
                Asignar Partner
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ============= DETALLE DE FACTURA ============= */}
        <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedInvoice && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <span className="font-mono">
                      {selectedInvoice.invoice_number || 'Factura sin número'}
                    </span>
                    <Badge className={estadoFactura(selectedInvoice.status).class}>
                      {estadoFactura(selectedInvoice.status).label}
                    </Badge>
                    {selectedInvoice.is_recurring && (
                      <Badge className="bg-purple-100 text-purple-800">Plantilla recurrente</Badge>
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    {selectedInvoice.customer_fiscal_name || selectedInvoice.customer_name || client?.name}
                    {selectedInvoice.customer?.tax_id ? ` · ${selectedInvoice.customer.tax_id}` : ''}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                  {/* Cabecera de datos */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Emisión</p>
                      <p className="font-medium">{formatearFecha(selectedInvoice.issue_date)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Vencimiento</p>
                      <p className="font-medium">{formatearFecha(selectedInvoice.due_date)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Forma de pago</p>
                      <p className="font-medium">{selectedInvoice.payment_method || '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Referencia</p>
                      <p className="font-medium break-words">{selectedInvoice.invoice_reference || '—'}</p>
                    </div>
                  </div>

                  {selectedInvoice.paid_date && (
                    <p className="text-sm text-green-700">
                      Cobrada el {formatearFecha(selectedInvoice.paid_date)}
                    </p>
                  )}

                  {/* Líneas */}
                  <div>
                    <p className="font-semibold mb-2">Líneas</p>
                    {loadingInvoiceDetail && !(selectedInvoice.line_items || selectedInvoice.items || []).length ? (
                      <Skeleton className="h-20 w-full" />
                    ) : (
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr className="text-left text-gray-500">
                              <th className="p-3 font-medium">Concepto</th>
                              <th className="p-3 font-medium text-right">Cant.</th>
                              <th className="p-3 font-medium text-right">Precio</th>
                              <th className="p-3 font-medium text-right">Dto.</th>
                              <th className="p-3 font-medium text-right">Importe</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedInvoice.line_items || selectedInvoice.items || []).map((linea, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="p-3">
                                  {linea.description || linea.product_name || 'Sin descripción'}
                                </td>
                                <td className="p-3 text-right">{linea.quantity ?? 1}</td>
                                <td className="p-3 text-right">{formatearImporte(linea.unit_price)}</td>
                                <td className="p-3 text-right">
                                  {linea.discount ? `${linea.discount}%` : '—'}
                                </td>
                                <td className="p-3 text-right font-medium">
                                  {formatearImporte(linea.subtotal)}
                                </td>
                              </tr>
                            ))}
                            {(selectedInvoice.line_items || selectedInvoice.items || []).length === 0 && (
                              <tr>
                                <td colSpan={5} className="p-4 text-center text-gray-500">
                                  Esta factura no tiene líneas guardadas
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Totales */}
                  <div className="flex justify-end">
                    <div className="w-full sm:w-64 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Base imponible</span>
                        <span>{formatearImporte(selectedInvoice.subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">IVA</span>
                        <span>
                          {formatearImporte(selectedInvoice.iva_amount ?? selectedInvoice.tax)}
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-1 text-base font-bold">
                        <span>Total</span>
                        <span>{formatearImporte(selectedInvoice.total)}</span>
                      </div>
                    </div>
                  </div>

                  {selectedInvoice.notes && (
                    <div className="text-sm">
                      <p className="text-gray-500">Notas</p>
                      <p className="whitespace-pre-wrap">{selectedInvoice.notes}</p>
                    </div>
                  )}

                  {selectedInvoice.invoice_number && !selectedInvoice.is_recurring && (
                    <div>
                      <p className="font-semibold mb-1">Pagos y devoluciones</p>
                      <HistorialCobros movimientos={movimientosDeCobro([selectedInvoice])} />
                    </div>
                  )}
                </div>

                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
                    Cerrar
                  </Button>
                  {/* El PDF solo existe si la factura tiene numero; las
                      plantillas recurrentes no lo tienen. */}
                  {selectedInvoice.invoice_number && (
                    <Button
                      onClick={() => handleDownloadInvoicePdf(selectedInvoice)}
                      disabled={downloadingPdf}
                    >
                      {downloadingPdf ? 'Descargando…' : 'Descargar PDF'}
                    </Button>
                  )}
                  {/* Lleva al buscador de Facturas ya filtrado, que es donde
                      se corrige. Sin numero ni referencia no hay nada por lo
                      que buscar, asi que el boton no se ofrece. */}
                  {(selectedInvoice.invoice_number || selectedInvoice.invoice_reference) && (
                    <Button
                      variant="outline"
                      onClick={() => navigate(
                        `/invoices?search=${encodeURIComponent(selectedInvoice.invoice_number || selectedInvoice.invoice_reference)}`
                      )}
                    >
                      Abrir en Facturas
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        <ConfirmDialog />
      </div>
    </Layout>
  );
}
